import React, { useEffect } from "react";

const WC_MOROCCO_STATES = {
  // WooCommerce region codes
  'MA-01': 'Tanger-Tétouan-Al Hoceïma',
  'MA-02': "L'Oriental",
  'MA-03': 'Fès-Meknès',
  'MA-04': 'Rabat-Salé-Kénitra',
  'MA-05': 'Béni Mellal-Khénifra',
  'MA-06': 'Casablanca-Settat',
  'MA-07': 'Marrakech-Safi',
  'MA-08': 'Drâa-Tafilalet',
  'MA-09': 'Souss-Massa',
  'MA-10': 'Guelmim-Oued Noun',
  'MA-11': 'Laâyoune-Sakia El Hamra',
  'MA-12': 'Dakhla-Oued Ed-Dahab',
  // City codes from deliveries system
  'MA001': 'Casablanca',
  'MA002': 'Dar Bouazza',
  'MA003': 'Bouskoura',
  'MA004': 'Mohamedia',
  'MA005': 'Benselimane',
  'MA006': 'Rabat',
  'MA007': 'Sale',
  'MA008': 'Temara',
  'MA009': 'Marrakech',
  'MA010': 'Ben Guerrir',
  'MA011': 'Agadir',
  'MA012': 'Tit Melil',
};

const getStateName = (stateCode) => {
  if (!stateCode) return '';
  return WC_MOROCCO_STATES[stateCode] || stateCode;
};

const ReceiptPrinter = () => {
  const order = JSON.parse(localStorage.getItem('order')) || {};
  console.log('Full order object:', order);
  console.log('Customer note:', order.customer_note);

  // Helper to decode HTML entities
  const decodeHtmlEntities = (text) => {
    if (!text) return '';
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    return textarea.value;
  };

  // Helper to format WooCommerce date
  const formatWooDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch (e) {
      return dateStr;
    }
  };

  // Extract order details from WooCommerce or internal format
  const orderNumber = order.number || order.id || order.orderNumber || '';
  const orderCode = order.number || order.orderCode || '';
  const orderDate = order.date_created ? formatWooDate(order.date_created) : (order.orderDate || '');
  const orderTime = order.date_created || order.orderTime || '';
  const customerNote = decodeHtmlEntities(order.customer_note || '');
  
  // Map WooCommerce line_items to products format
  const products = order.line_items ? order.line_items.map(item => ({
    title: item.name || '',
    quantity: item.quantity || 0,
    options: (item.meta_data || []).filter(m => 
      m && m.display_key && !String(m.display_key).startsWith('_')
    ).map(m => ({
      title: `${m.display_key}: ${m.display_value || m.value}`,
      quantity: 1
    }))
  })) : (order.products || []);

  useEffect(() => {
    let timer;

    const handlePrintClose = () => {
      window.close();
    };

    // Fallback: close after 60 seconds
    timer = setTimeout(() => {
      window.close();
    }, 60000);

    // Listen for afterprint
    window.addEventListener('afterprint', handlePrintClose);

    // Some browsers don't fire afterprint, so use focus as a backup
    window.addEventListener('focus', handlePrintClose);

    // Load QRCode library and generate QR codes
    const shipping = order?.shipping || {};
    const billing = order?.billing || {};
    const meta = Array.isArray(order?.meta_data) ? order.meta_data : [];
    const findMetaValue = (key) => {
      const m = meta.find(x => x && String(x.key).toLowerCase() === String(key).toLowerCase());
      return m?.value || '';
    };
    
    const rawPhone = shipping.shipping_phonenumber || shipping.phone || billing.phone || findMetaValue('_shipping_phonenumber') || findMetaValue('shipping_phonenumber');
    const phoneNumber = rawPhone ? rawPhone.replace(/\s+/g, '').replace(/^0/, '+212') : '';
    
    if (phoneNumber) {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/qrcodejs@1.0.0/qrcode.min.js';
      script.onload = () => {
        setTimeout(() => {
          if (window.QRCode) {
            const callEl = document.getElementById('qr-call');
            const waEl = document.getElementById('qr-whatsapp');
            
            if (callEl && !callEl.hasChildNodes()) {
              new window.QRCode(callEl, {
                text: `tel:${phoneNumber}`,
                width: 60,
                height: 60,
                colorDark: '#000000',
                colorLight: '#ffffff',
                correctLevel: window.QRCode.CorrectLevel.L
              });
            }
            
            if (waEl && !waEl.hasChildNodes()) {
              new window.QRCode(waEl, {
                text: `https://wa.me/${phoneNumber.replace(/\+/g, '')}`,
                width: 60,
                height: 60,
                colorDark: '#000000',
                colorLight: '#ffffff',
                correctLevel: window.QRCode.CorrectLevel.L
              });
            }
          }
        }, 200);
      };
      document.head.appendChild(script);
    }

    // Trigger print after a slight delay to ensure QR codes are generated
    setTimeout(() => {
      window.print();
    }, 800);

    // Cleanup listeners and timeout
    return () => {
      clearTimeout(timer);
      window.removeEventListener('afterprint', handlePrintClose);
      window.removeEventListener('focus', handlePrintClose);
    };
  }, []);

  const styles = {
    receipt: {
      width: "72mm",
      fontFamily: "arial",
      fontSize: "12px",
      padding: "10px",
      lineHeight: "1.4",
      color: "#000",
      backgroundColor: "#fff",
    },
    bold: { fontWeight: "bold" },
    section: { marginBottom: "10px" },
    hr: { border: "none", borderTop: "1px dashed #000", margin: "8px 0" },
    textCenter: { textAlign: "center" },
    textLeft: { textAlign: "left" },
    list: { paddingLeft: "20px", margin: "0" },
    listItem: { marginBottom: "5px" },
    header: { marginBottom: "20px", fontSize: "14px" },
    footer: { marginTop: "20px", fontSize: "12px", fontStyle: "italic" },
  };

  const platformLabel = (order?.productType || "GLOVO").toUpperCase();
  const hidePrices = order?.hidePrices === true;

  const getShippingInfo = (order) => {
    const shipping = order?.shipping || {};
    const billing = order?.billing || {};
    const customer = order?.customer || {};
    const meta = Array.isArray(order?.meta_data) ? order.meta_data : [];

    console.log('Shipping data:', shipping);
    console.log('Billing data:', billing);
    console.log('Meta data:', meta);

    const findMeta = (candidates = []) => {
      for (const k of candidates) {
        const m = meta.find(x => x && String(x.key).toLowerCase() === String(k).toLowerCase());
        if (m && m.value) return m.value;
      }
      return null;
    };

    // Prefer shipping info for customer details
    const firstName = shipping.first_name || billing.first_name || customer.first_name || '';
    const lastName = shipping.last_name || billing.last_name || customer.last_name || '';
    const name = (firstName || lastName) ? `${firstName} ${lastName}`.trim() : (shipping.name || customer.name || order?.CustomerName || '');

    const addressParts = [];
    // Use shipping address for WooCommerce orders
    const addr1 = shipping.address_1 || billing.address_1;
    const addr2 = shipping.address_2 || billing.address_2;
    const city = shipping.city || billing.city;
    const state = shipping.state || billing.state;
    
    if (addr1) addressParts.push(addr1);
    if (addr2) addressParts.push(addr2);
    if (city) addressParts.push(city);
    if (state) addressParts.push(getStateName(state));
    
    // fallback to meta keys that may contain shipping address
    if (addressParts.length === 0) {
      const addr = findMeta(['shipping_address', 'shipping_address_1', 'address_1', 'Adresse', 'adresse']);
      if (addr) addressParts.push(addr);
    }
    const address = addressParts.join(', ');

    // phone - prioritize shipping phone (matching WooOrdersView modal)
    const phoneCandidates = [
      shipping.shipping_phonenumber,
      shipping.phone,
      billing.phone,
      customer.phone,
      order?.phone,
      order?.customerPhone,
      findMeta(['_shipping_phonenumber','shipping_phonenumber','_billing_phone','billing_phone','phone','tel','telephone'])
    ];
    const phone = phoneCandidates.find(p => p) || '';

    console.log('Extracted - Name:', name, 'Address:', address, 'Phone:', phone);

    return { name: name || '', address: address || '', phone: phone || '' };
  };

  return (
    <div style={styles.receipt}>
      <div style={styles.header}>
        <div style={styles.textCenter}>
          <div style={styles.bold}>Le Céladon x {platformLabel}</div>
          <div>Site : www.le-celadon.ma</div>
          <h2> #{orderNumber}</h2>
        </div>
        {/* Only show store info for non-SITE orders (Glovo, etc.) */}
        {platformLabel !== 'SITE' && (
          <div style={styles.textLeft}>
            <div><span style={styles.bold}>Store:</span> {order?.store}</div>
            <div><span style={styles.bold}>Adresse:</span></div>
            <ul style={styles.list}>
              <li style={styles.listItem}>33 Rue 3 HAY LAAYOUNE, AIN BORJA CASABLANCA</li>
              <li style={styles.listItem}>9 Rue Ibnou Assakir, Les Princesses CASABLANCA</li>
              <li style={styles.listItem}>Carrefour Market One Hill, Carrefour ville verte, Bouskoura 25100</li>
            </ul>
            <div><span style={styles.bold}>Email:</span> contact@le-celadon.ma</div>
            <div><span style={styles.bold}>Téléphone:</span> +212 808 579 382/ +212 522 948 803</div>
          </div>
        )}
        {/* Show customer shipping/billing info */}
        {(() => {
          const ship = getShippingInfo(order);
          return (
            <div style={styles.textLeft}>
              {ship.name && (
                <div>
                  <span style={styles.bold}>Client:</span>{' '}
                  <span style={{ fontSize: '16px', fontWeight: 'bold' }}>{ship.name}</span>
                </div>
              )}
              {ship.address && <div><span style={styles.bold}>Adresse:</span> {ship.address}</div>}
              {ship.phone && (
                <div>
                  <span style={styles.bold}>Téléphone:</span>{' '}
                  <span style={{ fontSize: '16px', fontWeight: 'bold' }}>{ship.phone}</span>
                </div>
              )}
            </div>
          );
        })()}
        <div style={styles.textLeft}>
          <div><span style={styles.bold}>Code:</span> {orderCode}</div>
          <div><span style={styles.bold}>Date:</span> {orderDate} {orderTime}</div>
        </div>
        {/* Customer note if present */}
        {customerNote && (
          <div style={{ ...styles.textLeft, marginTop: '10px', padding: '8px', backgroundColor: '#fff9e6', border: '1px dashed #e0e0e0' }}>
            <div style={styles.bold}>Note:</div>
            <div style={{ fontSize: '11px', wordWrap: 'break-word' }}>{customerNote}</div>
          </div>
        )}
      </div>

      <div style={styles.hr}></div>

      <div style={styles.section}>
        <div style={styles.bold}>Produits :</div>
        {products?.map((product, idx) => (
          <div key={idx} style={{ marginBottom: "5px" }}>
            • {product.title} ({product.quantity})
            {product.options?.length > 0 &&
              product.options.map((opt, i) => (
                <div key={i} style={{ marginLeft: "10px" }}>
                  - {opt.title}
                </div>
              ))}
          </div>
        ))}
      </div>

      <div style={styles.hr}></div>

      {/* QR Codes for Call and WhatsApp */}
      {(() => {
        const ship = getShippingInfo(order);
        const phoneNumber = ship.phone ? ship.phone.replace(/\s+/g, '').replace(/^0/, '+212') : '';
        if (!phoneNumber) return null;
        return (
          <div style={{ ...styles.section, ...styles.textCenter }}>
            <div style={styles.bold}>Contact Client</div>
            <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: '10px' }}>
              <div style={{ textAlign: 'center' }}>
                <div id="qr-call" style={{ display: 'inline-block' }}></div>
                <div style={{ fontSize: '10px', marginTop: '5px' }}>Appeler</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div id="qr-whatsapp" style={{ display: 'inline-block' }}></div>
                <div style={{ fontSize: '10px', marginTop: '5px' }}>WhatsApp</div>
              </div>
            </div>
          </div>
        );
      })()}

      <div style={styles.textCenter}>
        <div style={styles.footer}>Merci pour votre commande 💐</div>
      </div>
    </div>
  );
};

export default ReceiptPrinter;
